import React, { useState } from 'react';
import { RefreshCw, Sunrise, Repeat, Baby, Calendar, Heart, ArrowLeft } from 'lucide-react';

const DreamQuestions = () => {
  const questionCategories = {
    waking: {
      title: "Feelings on Waking",
      icon: Sunrise,
      color: "from-amber-400 to-orange-400",
      bgColor: "from-amber-50 to-orange-50",
      borderColor: "border-amber-200",
      description: "Explore the immediate felt sense and body sensations upon waking from the dream.",
      questions: [
        "What was the quality of your breath when you first woke - open and easy, or tight and constricted?",
        "Where in your body did you first notice sensation or feeling upon waking?",
        "Did you wake with energy moving through you, or a sense of stillness and heaviness?",
        "Was the transition from dream to waking abrupt and jarring, or gentle and gradual?",
        "What arrived first - a thought about the dream, or a feeling in your body?",
        "What emotional atmosphere surrounded you in those first waking moments?",
        "When you opened your eyes, did you know immediately where you were, or was there disorientation?",
        "If you touch into the dream memory now, where does your body respond?"
      ]
    },
    themes: {
      title: "Familiar Themes",
      icon: Repeat,
      color: "from-purple-400 to-indigo-400",
      bgColor: "from-purple-50 to-indigo-50",
      borderColor: "border-purple-200",
      description: "Notice recurring patterns, feelings, and relational dynamics in your dream world.",
      questions: [
        "Is there a particular feeling tone that shows up again and again in your dreams?",
        "Do certain people or types of figures appear repeatedly across your dreams?",
        "Are there places or landscapes you return to in your dream world?",
        "What relational dynamics repeat themselves - perhaps ways of being close or distant?",
        "Do your dreams seem to be in conversation with each other, exploring the same question from different angles?",
        "Are there symbols or images that appear across multiple dreams with similar emotional weight?",
        "What emotional territories do you find yourself visiting again and again in dreams?",
        "Do your dreams return to particular struggles, longings, or questions from your waking life?"
      ]
    },
    childhood: {
      title: "Childhood Connections",
      icon: Baby,
      color: "from-pink-400 to-rose-400",
      bgColor: "from-pink-50 to-rose-50",
      borderColor: "border-pink-200",
      description: "Explore echoes of early relationships, attachment patterns, and childhood experiences.",
      questions: [
        "Do the relationships in this dream remind you of the relational world of your childhood?",
        "If your younger self could speak about this dream, what might they recognize or say?",
        "Are there childhood feelings alive in this dream - perhaps how you felt at a particular age?",
        "What longings from childhood might be present - to be seen, held, protected, or understood?",
        "Does the dream touch fears or worries you remember carrying as a child?",
        "Are there patterns of closeness and distance that echo your early attachment relationships?",
        "What family dynamics or roles feel familiar in the dream's relational field?",
        "What did your younger self need that appears or is absent in this dream?"
      ]
    },
    presentDay: {
      title: "Present-Day Life",
      icon: Calendar,
      color: "from-blue-400 to-cyan-400",
      bgColor: "from-blue-50 to-cyan-50",
      borderColor: "border-blue-200",
      description: "Connect the dream to your current life, relationships, and what's alive for you now.",
      questions: [
        "Which of your current relationships might be speaking through this dream?",
        "Is there a conflict or tension in your life right now that the dream seems to touch?",
        "What longing or desire from your present life feels alive in the dream?",
        "Are there decisions you're facing that the dream might be exploring or expressing?",
        "How do the roles you play in waking life - at work, at home, with others - appear in the dream?",
        "What in your current experience is calling for your attention that the dream illuminates?",
        "Where are you being invited to grow or stretch in your life, and does the dream reflect this edge?",
        "What is trying to emerge or become in you that the dream gives form to?"
      ]
    },
    trauma: {
      title: "Trauma & Healing",
      icon: Heart,
      color: "from-emerald-400 to-teal-400",
      bgColor: "from-emerald-50 to-teal-50",
      borderColor: "border-emerald-200",
      description: "Gently explore connections to difficult experiences and your system's healing process.",
      questions: [
        "Does the dream carry an atmosphere of danger, threat, or unsafety that feels familiar?",
        "What protective or defensive responses appear in the dream - fight, flight, freeze, or fawn?",
        "How activated or shut down does your nervous system feel in the dream world?",
        "Are there actions or responses that began but couldn't complete, leaving something unfinished?",
        "Might your system be working to integrate or make sense of difficult experiences through this dream?",
        "Do you have access to resources, support, or ground in the dream, or does overwhelm dominate?",
        "Where in your body do you sense the dream being held or stored somatically?",
        "Is there movement toward resolution, repair, or healing somewhere in the dream's unfolding?"
      ]
    }
  };

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  const nextQuestion = () => {
    const questions = questionCategories[selectedCategory].questions;
    setCurrentQuestionIndex((prev) => (prev + 1) % questions.length);
  };

  const selectCategory = (categoryKey) => {
    setSelectedCategory(categoryKey);
    setCurrentQuestionIndex(0);
  };

  const backToCategories = () => {
    setSelectedCategory(null);
    setCurrentQuestionIndex(0);
  };

  // Category selection view
  if (!selectedCategory) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-blue-50 flex items-center justify-center p-4">
        <div className="max-w-5xl w-full">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-light text-gray-800 mb-3">Dream Exploration</h1>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Choose a thread to follow. Which aspect of your dream feels most alive or calling for attention?
            </p>
          </div>

          {/* Category Cards */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {Object.entries(questionCategories).map(([key, category]) => {
              const IconComponent = category.icon;
              return (
                <button
                  key={key}
                  onClick={() => selectCategory(key)}
                  className={`bg-gradient-to-br ${category.bgColor} backdrop-blur-sm rounded-2xl shadow-lg p-6 border-2 ${category.borderColor} hover:shadow-2xl transition-all duration-300 transform hover:scale-105 text-left group`}
                  data-testid={`category-${key}`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-xl bg-gradient-to-br ${category.color} shadow-lg group-hover:scale-110 transition-transform`}>
                      <IconComponent className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-gray-800 mb-2 group-hover:text-gray-900">
                        {category.title}
                      </h3>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        {category.description}
                      </p>
                      <div className="mt-3 text-xs text-gray-500">
                        {category.questions.length} questions
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Footer Note */}
          <div className="text-center">
            <p className="text-sm text-gray-500 italic max-w-2xl mx-auto mb-4">
              There's no right place to start. Trust what draws you. You can explore different threads at any time.
            </p>
            <p className="text-xs text-gray-400">
              🔒 This tool stores no data. All interactions remain private and local to your device.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Question view
  const category = questionCategories[selectedCategory];
  const currentQuestion = category.questions[currentQuestionIndex];
  const IconComponent = category.icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-blue-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        {/* Back button */}
        <button
          onClick={backToCategories}
          className="mb-6 flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors group"
          data-testid="back-to-categories-btn"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          <span>Choose a different thread</span>
        </button>

        {/* Header with category */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-3">
            <div className={`p-2 rounded-lg bg-gradient-to-br ${category.color} shadow-md`}>
              <IconComponent className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-2xl font-semibold text-gray-800">{category.title}</h2>
          </div>
          <p className="text-sm text-gray-600">{category.description}</p>
        </div>

        {/* Main Card */}
        <div className={`bg-gradient-to-br ${category.bgColor} backdrop-blur-sm rounded-2xl shadow-xl p-8 md:p-12 border-2 ${category.borderColor}`}>
          {/* Question Progress */}
          <div className="mb-6 text-center">
            <span className="text-xs text-gray-500">
              Question {currentQuestionIndex + 1} of {category.questions.length}
            </span>
          </div>

          {/* Question Display */}
          <div className="mb-8">
            <div className="flex items-start gap-3 mb-6">
              <div className={`w-1 h-20 bg-gradient-to-b ${category.color} rounded-full flex-shrink-0`}></div>
              <p className="text-xl md:text-2xl text-gray-700 leading-relaxed font-light">
                {currentQuestion}
              </p>
            </div>
          </div>

          {/* Next Question Button */}
          <div className="flex justify-center">
            <button
              onClick={nextQuestion}
              className={`group flex items-center gap-2 bg-gradient-to-r ${category.color} hover:opacity-90 text-white px-8 py-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105`}
              data-testid="next-question-btn"
            >
              <RefreshCw className="w-5 h-5 group-hover:rotate-180 transition-transform duration-500" />
              <span className="font-medium">Next Question</span>
            </button>
          </div>
        </div>

        {/* Footer Note */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500 italic max-w-lg mx-auto">
            These questions invite curiosity and reflection. There are no right answers,
            only your own experience and what feels meaningful to explore.
          </p>
        </div>
      </div>
    </div>
  );
};

export default DreamQuestions;
